import type { StaffMember } from '@/types/staff';
import { staffApi } from './api/staff.api';
import { staffSchema, toStaffFormValues, type StaffFormValues } from './schemas/staff.schema';

/**
 * Switching an account off or back on — the one way both the register's row
 * menu and the profile do it.
 *
 * Deactivating keeps the record: the person stays on the list, is counted as
 * inactive, keeps their classes, and can be switched back on. Removing an
 * account (`staffApi.remove`) is a different, final act and is not this.
 *
 * The server takes the whole record on every update, so the status travels
 * with every other field exactly as it stands. An older record the form would
 * now refuse — reactivating one with no role, say — comes back as
 * `incomplete` rather than a request the server is certain to reject.
 */
export async function setStaffStatus(
  member: StaffMember,
  status: 'ACTIVE' | 'INACTIVE',
): Promise<AccountChangeOutcome> {
  return saveAccountWith(member, { status });
}

/**
 * Moving someone to another role — the Roles page's way of reassigning a
 * holder without opening their whole record. Same whole-record save as the
 * status, so their details are left exactly as they are.
 */
export async function setStaffRole(member: StaffMember, roleUuid: string): Promise<AccountChangeOutcome> {
  return saveAccountWith(member, { roleUuid });
}

export type AccountChangeOutcome = { ok: true; saved: StaffMember } | { ok: false; reason: 'incomplete' };

/** The account as it stands with one change, sent only if the form would accept it. */
async function saveAccountWith(
  member: StaffMember,
  change: Partial<StaffFormValues>,
): Promise<AccountChangeOutcome> {
  const parsed = staffSchema.safeParse({ ...toStaffFormValues(member), ...change });
  if (!parsed.success) return { ok: false, reason: 'incomplete' };

  const saved = await staffApi.update(member.uuid, parsed.data);
  return { ok: true, saved };
}
